import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../lib/auth-context';
import { useProject } from '../lib/project-context';
import { useDashboardStats } from '../lib/queries';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  FolderKanban, LineChart, BarChart3, TrendingUp, Plus, ArrowRight,
  FileBarChart, AlertTriangle, DollarSign, MapPin, Clock, ChevronDown,
  Layers, Bot, CheckCircle2, Sparkles, ChevronRight, Settings2, Play
} from 'lucide-react';
import { formatCurrency, formatPercent, STATUS_MAP } from '../lib/utils';
import { useModules } from '../lib/modules-context';
import { PostOnboardingBanner } from '../components/GuidedExperience';
import { ProjectScoreCard } from '../components/ProjectScore';
import { DecisionEngine } from '../components/DecisionEngine';
import { ContextualNudge } from '../components/Nudges';

const stagger = (i) => ({ initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25, delay: i * 0.04 } });

/* ════════════════════════════════════════════
   KPI Card
   ════════════════════════════════════════════ */
function KPI({ label, value, subtitle, icon: Icon, color, delay = 0 }) {
  return (
    <motion.div {...stagger(delay)}>
      <Card className="rounded-xl border-border/50 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`kpi-${label.toLowerCase().replace(/[\s\/]/g, '-')}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">{label}</p>
              <p className="text-2xl font-display font-semibold tabular-nums mt-1.5 truncate">{value}</p>
              {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}10` }}>
              <Icon className="h-4 w-4" style={{ color }} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ════════════════════════════════════════════
   Project Card
   ════════════════════════════════════════════ */
function ProjectCard({ project, onClick, delay = 0 }) {
  const statusInfo = STATUS_MAP[project.status] || { label: project.status, color: 'bg-gray-100 text-gray-700' };
  const scenarioCount = project.scenarios_count || project.scenario_count || 0;

  return (
    <motion.div {...stagger(delay)}>
      <Card
        className="rounded-xl border-border/50 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
        onClick={onClick}
        data-testid={`dashboard-project-${project.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="min-w-0">
              <h3 className="text-sm font-medium group-hover:text-foreground truncate">{project.name}</h3>
              {project.location && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3" /> {project.location}
                </p>
              )}
            </div>
            <Badge variant="secondary" className={`text-[10px] shrink-0 ${statusInfo.color}`}>{statusInfo.label}</Badge>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><Layers className="h-3 w-3" /> {scenarioCount} esc.</span>
            {project.updated_at && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> {new Date(project.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ════════════════════════════════════════════
   Contextual Project Block — adapts to active modules
   ════════════════════════════════════════════ */
function ContextualProjectBlock() {
  const { activeProject, activeScenario } = useProject();
  const { isActive } = useModules();
  const navigate = useNavigate();

  if (!activeProject) return null;

  const pid = activeProject.id;
  const sid = activeScenario?.id;

  return (
    <div className="mb-6 space-y-4">
      {/* Active project bar */}
      <Card className="rounded-xl border-border/50 shadow-sm" data-testid="active-project-card">
        <CardContent className="p-0">
          <div className="flex items-stretch">
            <div className="flex-1 p-4 flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--financial-light)' }}>
                <FolderKanban className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{activeProject.name}</span>
                  {activeScenario && (
                    <>
                      <ChevronRight className="w-3 h-3 shrink-0" style={{ color: 'var(--text-muted)' }} />
                      <span className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>{activeScenario.name}</span>
                    </>
                  )}
                </div>
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  {activeProject.location || 'Sin ubicación'}{activeScenario ? ' — Escenario activo' : ' — Selecciona un escenario en el proyecto'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-4 shrink-0" style={{ borderLeft: '1px solid var(--border-subtle)' }}>
              {sid && (
                <>
                  <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5" onClick={() => navigate(`/analyzer/wizard/${pid}/${sid}`)} data-testid="ctx-edit-scenario">
                    <Settings2 className="w-3.5 h-3.5" /> Ajustar escenario
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5" onClick={() => navigate('/analyzer')} data-testid="ctx-run-analysis">
                    <Play className="w-3.5 h-3.5" /> Analizar
                  </Button>
                  {isActive('market') && (
                    <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5" onClick={() => navigate('/market')} data-testid="ctx-market">
                      <TrendingUp className="w-3.5 h-3.5" /> Mercado
                    </Button>
                  )}
                </>
              )}
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5" onClick={() => navigate(`/projects/${pid}`)} data-testid="ctx-view-project">
                Ver proyecto <ArrowRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score + Decisions side by side — only if there's a scenario */}
      {sid && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ProjectScoreCard />
          <DecisionEngine />
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   Empty State (Onboarding)
   ════════════════════════════════════════════ */
function QuickActions() {
  const navigate = useNavigate();
  const { isActive } = useModules();

  const actions = [
    { label: 'Nuevo proyecto', icon: Plus, action: () => navigate('/projects'), testId: 'quick-new-project', always: true },
    { label: 'Ejecutar análisis', icon: LineChart, action: () => navigate('/analyzer'), testId: 'quick-run-analysis', module: 'financial' },
    { label: 'Generar reporte', icon: FileBarChart, action: () => navigate('/reports'), testId: 'quick-generate-report', module: 'financial' },
    { label: 'Comparables de mercado', icon: TrendingUp, action: () => navigate('/market'), testId: 'quick-market', module: 'market' },
    { label: 'Consultar Copilot', icon: Bot, action: () => navigate('/copilot'), testId: 'quick-open-copilot', module: 'financial' },
  ];

  const visible = actions.filter(a => a.always || isActive(a.module));

  return (
    <div className="space-y-2">
      {visible.map(item => (
        <Button key={item.label} variant="outline" className="w-full justify-start h-10 text-sm" onClick={item.action} data-testid={item.testId}>
          <item.icon className="mr-2 h-4 w-4" style={{ color: 'var(--primary-hex)' }} />
          {item.label}
        </Button>
      ))}
    </div>
  );
}

function EmptyDashboard({ userName }) {
  const navigate = useNavigate();

  return (
    <div className="px-6 lg:px-8 py-6 max-w-[900px] mx-auto" data-testid="dashboard-empty">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="text-center pt-8 mb-10">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: 'rgba(15,157,170,0.08)' }}>
            <Sparkles className="h-7 w-7" style={{ color: 'var(--primary-hex)' }} />
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mb-2">
            Bienvenido a Stratika
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Crea tu primer proyecto inmobiliario para empezar a analizar la viabilidad financiera de tus desarrollos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { step: '1', title: 'Crear proyecto', desc: 'Define nombre, ubicación y tipo de desarrollo', icon: FolderKanban },
            { step: '2', title: 'Configurar escenarios', desc: 'Ingresa supuestos financieros con el wizard de 11 pasos', icon: Layers },
            { step: '3', title: 'Analizar resultados', desc: 'Ejecuta el motor y obtén métricas, flujos y alertas', icon: LineChart },
          ].map((s, i) => (
            <Card key={i} className="rounded-xl border-border/50">
              <CardContent className="p-5 text-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-3 text-xs font-bold text-white" style={{ background: 'var(--primary-hex)' }}>
                  {s.step}
                </div>
                <h3 className="text-sm font-medium mb-1">{s.title}</h3>
                <p className="text-xs text-muted-foreground">{s.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center">
          <Button
            onClick={() => navigate('/projects')}
            className="h-11 px-6 text-white hover:opacity-90 rounded-lg"
            style={{ background: 'var(--primary-hex)' }}
            data-testid="onboarding-create-project"
          >
            <Plus className="mr-2 h-4 w-4" /> Crear mi primer proyecto
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN DASHBOARD
   ════════════════════════════════════════════ */
export default function DashboardPage() {
  const { user } = useAuth();
  const { setActiveProject } = useProject();
  const navigate = useNavigate();
  const { data: stats, isLoading: loading } = useDashboardStats();

  const handleProjectClick = (project) => {
    setActiveProject(project);
    navigate(`/projects/${project.id}`);
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="px-6 lg:px-8 py-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-72 mb-1" />
        <Skeleton className="h-4 w-40 mb-8" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  // ── Empty state ──
  if (!stats || stats.projects_count === 0) {
    return <EmptyDashboard userName={user?.full_name?.split(' ')[0]} />;
  }

  const recentProjects = stats.recent_projects || [];
  const latestRuns = stats.latest_runs || [];
  const recentActivity = stats.recent_activity || [];

  return (
    <div className="px-6 lg:px-8 py-6 max-w-[1440px]" data-testid="dashboard-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
        className="flex items-start justify-between mb-7">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="dashboard-greeting">
            Bienvenido, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{user?.company_name}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="text-white hover:opacity-90 h-9" style={{ background: 'var(--accent-primary)' }} data-testid="dashboard-new-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Nuevo <ChevronDown className="ml-1 h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/projects')} data-testid="new-project-action">
              <FolderKanban className="mr-2 h-4 w-4" /> Nuevo proyecto
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/analyzer')} data-testid="new-analysis-action">
              <LineChart className="mr-2 h-4 w-4" /> Nuevo análisis
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/reports')} data-testid="new-report-action">
              <FileBarChart className="mr-2 h-4 w-4" /> Generar reporte
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </motion.div>

      {/* Post-onboarding suggestions */}
      <PostOnboardingBanner projectName={recentProjects[0]?.name} />

      {/* Contextual project info — only when project active + has scenario */}
      <ContextualProjectBlock />

      {/* Contextual nudge */}
      <div className="mb-4"><ContextualNudge nudgeKey="dashboard:complete_analysis" /></div>

      {/* KPI Cards */}
      {stats.metrics_reset_at && (
        <div className="mb-3 px-3 py-2 rounded-lg text-[11px] text-muted-foreground flex items-center gap-2" style={{ background: 'var(--accent-subtle)', border: '1px solid var(--accent-primary)20' }} data-testid="metrics-reset-banner">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--accent-primary)' }} />
          <span>Contadores reiniciados el {new Date(stats.metrics_reset_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}. Los widgets muestran solo actividad posterior a esa fecha. El histórico completo se conserva.</span>
        </div>
      )}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="dashboard-kpis">
        <KPI
          label="Proyectos activos"
          value={stats.projects_count}
          subtitle={`${stats.scenarios_count} escenarios`}
          icon={FolderKanban}
          color="var(--primary-hex)"
          delay={0}
        />
        <KPI
          label="Capital analizado"
          value={stats.total_revenue_analyzed > 0 ? formatCurrency(stats.total_revenue_analyzed, 0) : '--'}
          subtitle={`${stats.analysis_count} ejecuciones`}
          icon={DollarSign}
          color="var(--success)"
          delay={1}
        />
        <KPI
          label="TIR promedio"
          value={stats.avg_irr != null ? formatPercent(stats.avg_irr) : 'N/A'}
          subtitle={stats.avg_irr != null ? 'Últimos 10 análisis' : 'Sin análisis con TIR válida'}
          icon={TrendingUp}
          color="var(--warning-color)"
          delay={2}
        />
        <KPI
          label="Reportes"
          value={stats.reports_count}
          subtitle={stats.alerts_count > 0 ? `${stats.alerts_count} alertas` : 'Sin alertas'}
          icon={stats.alerts_count > 0 ? AlertTriangle : FileBarChart}
          color={stats.alerts_count > 0 ? 'var(--danger)' : '#6366f1'}
          delay={3}
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left: Projects */}
        <div className="lg:col-span-8 space-y-5">
          {/* Project Grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Proyectos
              </h2>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/projects')} data-testid="view-all-projects">
                Ver todos <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="dashboard-projects-grid">
              {recentProjects.map((p, i) => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  onClick={() => handleProjectClick(p)}
                  delay={i + 4}
                />
              ))}
            </div>
          </div>

          {/* Latest Analysis Runs */}
          {latestRuns.length > 0 && (
            <motion.div {...stagger(8)}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Últimos análisis
                </h2>
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/analyzer')} data-testid="view-all-analyses">
                  Ver todos <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
              <Card className="rounded-xl border-border/50 shadow-sm">
                <CardContent className="p-0">
                  <div className="divide-y divide-border/50">
                    {latestRuns.map((run, i) => {
                      const metrics = run.metrics || {};
                      const irr = metrics.irr_annual;
                      const isGood = irr != null && irr > 0 && irr < 100;
                      return (
                        <div key={run.id || i} className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30 transition-colors" data-testid={`analysis-run-${i}`}>
                          <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: run.status === 'completed' ? 'var(--success)' : 'var(--danger)' }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{run.scenario_name || 'Escenario'}</p>
                            <p className="text-[10px] text-muted-foreground">v{run.engine_version} | {run.duration_ms}ms</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-display font-semibold tabular-nums" style={{ color: isGood ? 'var(--success)' : '' }}>
                              {irr != null ? formatPercent(irr) : '--'}
                            </p>
                            <p className="text-[10px] text-muted-foreground">TIR inv.</p>
                          </div>
                          <div className="text-right shrink-0 hidden md:block">
                            <p className="text-xs font-medium tabular-nums">
                              {metrics.total_revenue ? formatCurrency(metrics.total_revenue, 0) : '--'}
                            </p>
                            <p className="text-[10px] text-muted-foreground">Ingreso</p>
                          </div>
                          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0 hidden lg:block">
                            {run.created_at ? new Date(run.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Right Column */}
        <div className="lg:col-span-4 space-y-5">
          {/* Quick Actions */}
          <motion.div {...stagger(6)}>
            <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Acciones rápidas
            </h2>
            <QuickActions />
          </motion.div>

          {/* Recent Activity */}
          <motion.div {...stagger(7)}>
            <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Actividad reciente
            </h2>
            <Card className="rounded-xl border-border/50 shadow-sm">
              <CardContent className="p-3">
                {recentActivity.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-6">Sin actividad registrada</p>
                ) : (
                  <div className="space-y-3">
                    {recentActivity.slice(0, 6).map((log, i) => {
                      const actionLabel = {
                        create: 'Creó', update: 'Actualizó', delete: 'Eliminó',
                        run_analysis: 'Ejecutó análisis', duplicate: 'Duplicó',
                        generate_report: 'Generó reporte', archive: 'Archivó', restore: 'Restauró',
                        login: 'Inició sesión', register: 'Se registró',
                        'update scenario': 'Actualizó escenario', 'create scenario': 'Creó escenario',
                        'delete scenario': 'Eliminó escenario', 'update assumptions': 'Actualizó supuestos',
                        'create project': 'Creó proyecto', 'update project': 'Actualizó proyecto',
                        'delete project': 'Eliminó proyecto',
                      }[log.action] || log.action;
                      const entityLabel = {
                        project: 'proyecto', scenario: 'escenario', report: 'reporte',
                        analysis_run: 'análisis', user: 'usuario', company: 'empresa',
                        analysis: 'análisis',
                      }[log.entity_type] || log.entity_type;
                      const dotColor = log.action === 'delete' ? 'var(--danger)' : log.action === 'create' ? 'var(--success)' : 'var(--primary-hex)';

                      return (
                        <div key={log.id || i} className="flex items-start gap-2.5" data-testid={`activity-${i}`}>
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: dotColor }} />
                          <div className="min-w-0">
                            <p className="text-xs">
                              <span className="font-medium">{actionLabel}</span>{' '}
                              <span className="text-muted-foreground">{entityLabel}</span>
                              {log.project_name && <span className="text-muted-foreground"> en {log.project_name}</span>}
                            </p>
                            <p className="text-[10px] text-muted-foreground tabular-nums">
                              {log.created_at ? new Date(log.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
